import { useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { ArrowLeft } from 'lucide-react'
import { Button, Card } from './ui'

const Privacy = () => {
  const navigate = useNavigate()

  return (
    <>
      <Helmet>
        <title>Privacy Policy - YouWont</title>
        <meta name="description" content="Privacy policy and cookie information for YouWont" />
      </Helmet>

      <div className="py-10 sm:py-14 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="mb-6 -ml-2">
            <ArrowLeft size={16} className="mr-1.5" />
            Back to Home
          </Button>

          <Card className="!p-8 sm:!p-10">
            <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100 mb-8">Privacy Policy</h1>

            <div className="space-y-8 text-slate-600 dark:text-slate-400">
              <section>
                <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-200 mb-3">Cookie Policy</h2>
                <p>This website uses cookies to enhance your browsing experience and provide essential functionality.</p>
              </section>

              <section>
                <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mb-2">What are cookies?</h3>
                <p>Cookies are small text files stored on your device when you visit a website. They help the website remember your preferences and provide a better user experience.</p>
              </section>

              <section>
                <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mb-3">How we use cookies</h3>
                <ul className="list-disc list-inside space-y-2">
                  <li><strong className="text-slate-700 dark:text-slate-300">Essential cookies:</strong> Required for the website to function properly (e.g., authentication tokens, user preferences)</li>
                  <li><strong className="text-slate-700 dark:text-slate-300">Functional cookies:</strong> Remember your choices and settings to improve your experience</li>
                  <li><strong className="text-slate-700 dark:text-slate-300">Analytics cookies:</strong> Help us understand how visitors use our website</li>
                </ul>
              </section>

              <section>
                <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mb-3">Types of cookies we use</h3>
                <div className="space-y-3">
                  <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4">
                    <h4 className="font-semibold text-slate-700 dark:text-slate-300 mb-1">Authentication Cookies</h4>
                    <p className="text-sm mb-1">Store your login tokens to keep you signed in to your dashboard.</p>
                    <p className="text-xs text-slate-400 dark:text-slate-500">
                      Example: <code className="bg-slate-100 dark:bg-slate-700 px-1 rounded">qr:yourhandle</code> — stores your edit token
                    </p>
                  </div>
                  <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4">
                    <h4 className="font-semibold text-slate-700 dark:text-slate-300 mb-1">Preference Cookies</h4>
                    <p className="text-sm mb-1">Remember your cookie consent choice and other preferences.</p>
                    <p className="text-xs text-slate-400 dark:text-slate-500">
                      Example: <code className="bg-slate-100 dark:bg-slate-700 px-1 rounded">qr-app-cookies-consented</code> — stores your cookie consent
                    </p>
                  </div>
                </div>
              </section>

              <section>
                <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mb-3">Managing cookies</h3>
                <p className="mb-3">You can control and manage cookies in your browser settings. Most browsers allow you to:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>View and delete existing cookies</li>
                  <li>Block cookies from specific websites</li>
                  <li>Block all cookies</li>
                  <li>Set preferences for different types of cookies</li>
                </ul>
              </section>

              <section>
                <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mb-3">Third-party services</h3>
                <p className="mb-3">We use the following third-party services that may set their own cookies:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li><strong className="text-slate-700 dark:text-slate-300">AWS CloudFront:</strong> Content delivery and caching</li>
                  <li><strong className="text-slate-700 dark:text-slate-300">AWS S3:</strong> Image storage and delivery</li>
                  <li><strong className="text-slate-700 dark:text-slate-300">AWS Lambda:</strong> Backend API services</li>
                </ul>
              </section>

              <section>
                <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mb-2">Updates to this policy</h3>
                <p>We may update this privacy policy from time to time. Any changes will be posted on this page with an updated revision date.</p>
              </section>

              <section>
                <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mb-2">Contact us</h3>
                <p>If you have any questions about our privacy policy or how we use cookies, please contact us through your dashboard.</p>
              </section>

              <div className="pt-6 border-t border-slate-200 dark:border-slate-700">
                <p className="text-sm text-slate-400 dark:text-slate-500">Last updated: {new Date().toLocaleDateString()}</p>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </>
  )
}

export default Privacy
